import { Component, OnInit, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import * as Sentry from '@sentry/angular';

import {
  faArrowCircleLeft,
  faCheckCircle,
  faCircleExclamation,
  faCircleInfo,
  faEraser,
  faSpinner,
  faUserGraduate,
  faUserPlus,
  faCircleXmark,
  faUsers,
  faCircleMinus,
  faCaretSquareRight,
  faCaretSquareDown,
  faQuestionCircle,
} from '@fortawesome/free-solid-svg-icons';

import { StudentsService } from 'src/app/core/services/students.service';
import { Student } from 'src/app/core/models/student.model';
import { SessionStorageService } from 'src/app/core/services/session-storage.service';
import { ManagementUser } from 'src/app/core/models/management-user.model';

import introJs from 'intro.js';
import { coteacherIntroSteps } from './coteacher.intro-steps';

@Component({
    selector: 'wf-manage-coteachers',
    templateUrl: './coteacher.component.html',
    styleUrls: ['./coteacher.component.css'],
    standalone: false
})
export class AssignCoTeacherComponent implements OnInit, AfterViewInit {
  managementUser: ManagementUser | null = null ;
  isParentUser: boolean = false ;
  isDeleting: boolean = false ;
  userRole: string = '' ;
  messageTitle: string = '' ;
  message: string = '' ;
  messageStatus: string = '' ;
  students: Student[] = [] ;
  selectedStudents: Set<number> = new Set<number>();
  studentsSelected: boolean = false ;
  studentIcon = faUserGraduate ;
  addTeacherIcon = faUserPlus ;
  loadingIcon = faSpinner ;
  successIcon = faCheckCircle ;
  errorIcon = faCircleExclamation ;
  closeIcon = faCircleXmark ;
  coTeacherIcon = faUsers ;
  removeCoTeacherIcon = faCircleMinus
  clearSelectionIcon = faEraser ;
  infoIcon = faCircleInfo ;
  backIcon = faArrowCircleLeft ;
  expandIcon = faCaretSquareRight ;
  collapseIcon = faCaretSquareDown ;
  helpIcon = faQuestionCircle ;

  // Co-Teacher props
  isAddingCoTeacher: boolean = false ;
  showAddCoTeacherDialog: boolean = false ;
  showAssignStudentsDialog: boolean = false ;
  selectedCoTeacherId?: number;
  myCoTeacherData: { [key: number]: (number | null)[] } = {};
  teachersWithAllAccess: any[] = [];
  filteredTeachers: any[] = [];

  selectedStudentForRevoke: number | null = null;
  studentCoTeacherMap: { [key: number]: (number | null)[] } = {};
  searchTerm: string = '';
  introTour: any;

  constructor(
    private route: ActivatedRoute,
    private studentService: StudentsService,
    private sessionService: SessionStorageService,
    private changeDetectorRef: ChangeDetectorRef,
  ) { }

  ngOnInit(): void {
    this.managementUser = this.sessionService.getUserData() ;
    if (this.managementUser)
    {
      this.isParentUser = this.managementUser.isParentUser() ;
      this.userRole = this.managementUser.userRole ;
    }

    this.students = this.route.snapshot.data['resolveData'].students ;
    this.myCoTeacherData = this.route.snapshot.data['resolveData'].myCoTeacherData ;
    const teachers: ManagementUser[] = this.route.snapshot.data['resolveData'].teachers ;

    if (teachers.length) {
      this.filteredTeachers = teachers.map((teacher: ManagementUser) => {
        return {
          ...teacher,
          fullName: `${teacher.firstName} ${teacher.lastName}`
        }
      })
      .filter((teacher) => {
        return teacher.userID !== this.managementUser?.userID ;
      }) ;

      this.teachersWithAllAccess = this.filteredTeachers.filter((teacher) => {
        if (this.myCoTeacherData[teacher.userID] && this.myCoTeacherData[teacher.userID]?.includes(null)) {
          return true ;
        }
        return false ;
      }) ;

      this.studentCoTeacherMap = this.generateStudentCoTeacherMap() ;
    }
  }

  ngAfterViewInit(): void {
    this.setupIntroTour();
  }

  private filterIntroStepsByExistingElements(steps: any[]): any[] {
    return steps.filter(step => {
      if (!step.element) {
        return true;
      }
      
      try {
        const element = document.querySelector(step.element);
        return element !== null;
      } catch (error) {
        console.warn(`Invalid selector in intro step: ${step.element}`, error);
        return false;
      }
    });
  }

  setupIntroTour() {
    const intro = introJs();
    intro.setOptions({
      steps: coteacherIntroSteps,
      nextLabel: 'Next →',
      prevLabel: '← Back',
      doneLabel: 'Done',
      exitOnOverlayClick: false,
      showStepNumbers: true,
      disableInteraction: false,
      scrollToElement: true,
    });
    this.introTour = intro;
  }

  startTour() {
    const filteredSteps = this.filterIntroStepsByExistingElements(coteacherIntroSteps);
    this.introTour.setOptions({ steps: filteredSteps });
    this.introTour.start();
  }

  studentToggled(studentId: number) {
    if (!this.selectedStudents.has(studentId)) {
      this.selectedStudents.add(studentId);
    } else {
      this.selectedStudents.delete(studentId);
    }

    this.studentsSelected = this.selectedStudents.size > 0 ;
  }

  clearStudentSelection() {
    this.selectedStudents.clear() ;
  }

  searchStudents() {
    if (!this.searchTerm) {
      return this.students;
    }

    return this.students.filter((student) => {
      return student.firstName.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
        student.lastName.toLowerCase().includes(this.searchTerm.toLowerCase());
    })
  }

  clearMessage() {
    this.message = '';
    this.messageTitle = '';
    this.messageStatus = '';
  }

  openAddCoTeacherDialog() {
    // this.isAddingCoTeacher = true ; // should control spinner when service call is in progress
    this.showAddCoTeacherDialog = true ;
    this.selectedStudents.clear() ;
  }

  hideCoTeacherDialog() {
    // this.isAddingCoTeacher = false ;
    this.selectedStudents.clear() ;
    this.showAddCoTeacherDialog = false ;
  }

  openAssignStudentsDialog() {
    this.showAssignStudentsDialog = true ;
  }

  hideAssignStudentsDialog() {
    this.showAssignStudentsDialog = false ;
  }

  assignCoTeacherToSelectedStudents() {
    this.isAddingCoTeacher = true;
    const teacherId = this.managementUser?.userID;

    if (!teacherId || !this.selectedCoTeacherId || this.selectedStudents.size === 0) {
      this.isAddingCoTeacher = false;
      return;
    }

    // block attempts to assign students if selected teacher already has full access
    if (this.myCoTeacherData[this.selectedCoTeacherId]?.includes(null)) {
      this.message = 'The selected teacher already has full co-teacher access.';
      this.messageTitle = 'Duplicate Assignment';
      this.messageStatus = 'error';
      this.isAddingCoTeacher = false;
      this.hideAssignStudentsDialog();
      return;
    }

    // Remove selected student ID's that are already assigned to the selected teacher
    for (const studentId of this.selectedStudents) {
      if (this.myCoTeacherData[this.selectedCoTeacherId]?.includes(studentId)) {
        this.selectedStudents.delete(studentId);
      } else if (this.myCoTeacherData[this.selectedCoTeacherId]?.includes(null)) {
        this.selectedStudents.delete(studentId);
      }

      // It's possible that no students are selected after removing duplicates
    if (this.selectedStudents.size === 0) {
      this.isAddingCoTeacher = false;
      this.selectedStudents.clear() ;
      this.studentsSelected = false;
      this.hideAssignStudentsDialog();
      return;
    }
    }

    // It's possible that no students are selected after removing duplicates
    if (this.selectedStudents.size === 0) {
      this.isAddingCoTeacher = false;
      this.selectedStudents.clear() ;
      this.studentsSelected = false;
      this.hideAssignStudentsDialog();
      return;
    }

    // Add the teacher to the selected students
    this.studentService.assignCoTeacherAccess(this.selectedCoTeacherId, Array.from(this.selectedStudents)).subscribe({
      next: (coteacherMap) => {
        // Update myCoTeacherData with newly assigned student IDs
        this.myCoTeacherData = coteacherMap ;

        // Update studentCoTeacherMap
        this.studentCoTeacherMap = this.generateStudentCoTeacherMap();

        this.messageTitle = 'Co-Teacher Assignment';
        this.messageStatus = 'success';
        this.message = `Successfully assigned selected students to co-teacher`;

        this.isAddingCoTeacher = false;
        this.selectedStudents.clear();
        this.studentsSelected = false;
        this.hideAssignStudentsDialog();
      },
      error: (error) => {
        // Handle deadlock error
        if (error?.status === 500 && error?.error?.includes('Deadlock')) {
          this.messageTitle = 'Co-Teacher Assignment';
          this.messageStatus = 'error';
          this.message = 'There was a conflict assigning the co-teacher. Please try again.';
        } else {
          this.messageTitle = 'Error';
          this.messageStatus = 'error';
          this.message = 'Failed to assign co-teacher. Please try again later.';
        }
        this.isAddingCoTeacher = false;
      }
    });
  }

  assignCoTeacherAccessToAllStudents(selectedTeacherId?: number) {
    this.isAddingCoTeacher = true;

    if (!selectedTeacherId) {
      Sentry.captureException('No Teacher ID passed when attempting to assign co-teacher access to all Students') ;
      return ;
    }

    // We don't want to add a duplicate co-teacher, so check if the teacher is already assigned
    // If it is, then we check if it's an 'all students' permission. Which would be if the array
    // for the teacher contains a null value
    if (this.myCoTeacherData[selectedTeacherId]) {
      if (this.myCoTeacherData[selectedTeacherId].includes(null)) {
        this.messageTitle = 'Co-Teacher Assignment';
        this.messageStatus = 'error';
        this.message = 'The selected teacher already has access to all students.';
        this.isAddingCoTeacher = false;
        this.hideCoTeacherDialog();
        return ;
      }
    }

    this.studentService.assignCoTeacherAccess(selectedTeacherId, []).subscribe({
      next: (coteacherMap) => {
        // Update teachersWithAccess array
        this.myCoTeacherData = coteacherMap ;
        const selectedTeacher = this.filteredTeachers.find(t => t.userID === selectedTeacherId);
        if (selectedTeacher && !this.teachersWithAllAccess.includes(selectedTeacher)) {
          this.teachersWithAllAccess.push(selectedTeacher);
        }

        this.studentCoTeacherMap = this.generateStudentCoTeacherMap() ;

        this.messageTitle = 'Co-Teacher Assignment';
        this.messageStatus = 'success';
        this.message = `Successfully assigned co-teacher`;
      }
    });

    this.isAddingCoTeacher = false;
    this.selectedStudents.clear();
    this.studentsSelected = false;

    this.hideCoTeacherDialog();

    this.studentService.getGrantedCoTeacherData().subscribe({
      next: (coteacherMap) => {
        this.myCoTeacherData = coteacherMap ;
        this.studentCoTeacherMap = this.generateStudentCoTeacherMap() ;
        this.changeDetectorRef.detectChanges();
        this.changeDetectorRef.markForCheck();
      }
    })
  }

  revokeCoTeacherAccess(selectedTeacherId: number) {
    this.isAddingCoTeacher = true;
    this.isDeleting = true ;

    this.studentService.revokeCoTeacherAccess(selectedTeacherId).subscribe({
      next: (coteacherMap) => {
        // Remove teacher from teachersWithAccess array
        this.myCoTeacherData = coteacherMap ;
        this.teachersWithAllAccess = this.teachersWithAllAccess.filter(
          teacher => teacher.userID !== selectedTeacherId
        );

        const revokedTeacher = this.filteredTeachers.find(t => t.userID === selectedTeacherId);
        this.messageTitle = 'Co-Teacher Removed';
        this.messageStatus = 'success';
        this.message = `Revoked report access for ${revokedTeacher?.fullName || 'teacher'}.`;

        this.isDeleting = false;
      },
      error: (error) => {
        Sentry.captureException(error) ;
        this.messageTitle = 'Error Removing Co-Teacher';
        this.messageStatus = 'error' ;
        this.message = 'An error occurred while removing co-teacher access. Please try again later.';

        this.isDeleting = false ;
      }
    });

    this.isAddingCoTeacher = false;
    this.selectedStudents.clear();
    this.studentsSelected = false;
  }

  setSelectedCoTeacher(teacherId: number) {
    this.selectedCoTeacherId = teacherId ;
  }

  getStudentCoTeachers(studentId: number): any[] {
    return this.studentCoTeacherMap[studentId] || [];
  }

  generateStudentCoTeacherMap(): { [key: number]: (number | null)[] } {
    let studentCoTeacherMap: { [key: number]: (number | null)[] } = {} ;

    // From the co-teacher map, generate a map of student -> teachers
    Object.keys(this.myCoTeacherData).forEach((teacherIdStr: string) => {
      const teacherId = parseInt(teacherIdStr) ;
      this.myCoTeacherData[teacherId].forEach((studentId) => {
        if (studentId === null) return ;

        studentCoTeacherMap[studentId] = this.filteredTeachers.filter(teacher => {
          return this.myCoTeacherData[teacher.userID]?.includes(studentId);
        });
      });
    }) ;

    return studentCoTeacherMap ;
  }

  hasCoTeachers(studentId: number): boolean {
    return (this.studentCoTeacherMap[studentId] || []).length > 0;
  }

  toggleStudentCoTeacherList(studentId: number) {
    this.selectedStudentForRevoke = this.selectedStudentForRevoke === studentId ? null : studentId;
  }

  revokeCoTeacherAccessForStudent(teacherId: number, studentId: number) {
    this.isAddingCoTeacher = true;

    this.studentService.revokeCoTeacherAccess(teacherId, studentId).subscribe({
      next: (coteacherMap) => {
        // Update the coTeacherData to remove this student
        this.myCoTeacherData = coteacherMap ;
        this.studentCoTeacherMap = this.generateStudentCoTeacherMap() ;

        const revokedTeacher = this.filteredTeachers.find(t => t.userID === teacherId);
        const student = this.students.find(s => s.userID === studentId);

        if (!this.hasCoTeachers(studentId)) {
          this.selectedStudentForRevoke = null ;
        }

        this.messageTitle = 'Co-Teacher Access Updated';
        this.messageStatus = 'success';
        this.message = `Revoked ${revokedTeacher?.fullName || 'teacher'}'s access to ${student?.firstName}'s reports.`;

        this.isAddingCoTeacher = false;
      },
      error: (error) => {
        this.messageTitle = 'Error';
        this.messageStatus = 'error';
        this.message = 'Failed to revoke co-teacher access. Please try again later.';
        this.isAddingCoTeacher = false;
      }
    });
  }
}
